/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import * as React from 'react';
import { tryParseJson } from 'src/core/utils/base';
import { getStorage } from 'src/core/utils/getStorage';
import { useStateRef } from 'src/luis/projects/analytics/hooks/useStateRef';
import { FiltersTypes } from 'src/luis/projects/analytics/types/Filters';

type UIState = {
    filterRatio: string;
    appliedFilter: FiltersTypes;
    selectedIntentIndex: number;
};

/**
 * Keeps track of the UI state in the dashboard page for the
 * seek of retrieving this state anytime the user return back
 * to the page in the same session (without refreshing the page).
 *
 * This hook take care of the intents with errors card state specifically.
 */
export const useIntentsWithErrorsCardTracker = () => {
    const [uiState, setUiState, uiStateRef] = useStateRef<UIState>({
        filterRatio: '—',
        appliedFilter: 'mostProblematic',
        selectedIntentIndex: 0
    });

    const sessionStorage = getStorage('sessionStorage');

    React.useEffect(() => {
        const previouslyStoredState = tryParseJson<UIState>(sessionStorage.getData('analyticsIntentsWithErrorsCardTracker'));
        if (previouslyStoredState) {
            setUiState(previouslyStoredState);
        }

        return () => sessionStorage.setData('analyticsIntentsWithErrorsCardTracker', JSON.stringify(uiStateRef.current));
    }, []);

    /**
     * Sets the current applied filter.
     */
    const setAppliedFilter = (filter: FiltersTypes) => setUiState({ ...uiStateRef.current, appliedFilter: filter });

    /**
     * Sets the current used ratio to filter on.
     */
    const setFilterRatio = (ratio: string) => setUiState({ ...uiStateRef.current, filterRatio: ratio });

    /**
     * Sets the selected intent index to show more details about.
     */
    const setSelectedIntentIndex = (index: number) => setUiState({ ...uiStateRef.current, selectedIntentIndex: index });

    return {
        uiState,
        setFilterRatio,
        setAppliedFilter,
        setSelectedIntentIndex
    };
};
